/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { usePlacesWidget } from "react-google-autocomplete";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";
import { TextFieldInput } from "./TextFieldInput";
import {
  addressParser,
  GooglePlaceError,
  getErrorMsg,
  IGooglePlaceResponse,
} from "../utils/googlePlacesUtil";
import config from "../config";

export interface IGooglePlaceInputProps {
  onPlaceChange?: (e: IGooglePlaceResponse) => void;
  placeholder?: string;
  hasError?: string;
  label?: string;
  _defaultValue?: IGooglePlaceResponse;
  style?: React.CSSProperties;
}

export function GooglePlaceInput(props: IGooglePlaceInputProps) {
  const { onPlaceChange, hasError, _defaultValue, ...rest } = props;
  const [placeId, setPlaceId] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [address, setAddress] = useState<IGooglePlaceResponse>();

  useEffect(() => {
    setError(hasError);
  }, [hasError]);

  useEffect(() => {
    if (
      _defaultValue?.PostCode &&
      _defaultValue?.PostCode !== address?.PostCode
    ) {
      setAddress(_defaultValue);
    }
  }, [address, _defaultValue]);

  const { placesService } = usePlacesService({
    apiKey: config.googleApiKey,
    options: {
      componentRestrictions: { country: config.country },
    },
  });

  const { ref: materialRef } = usePlacesWidget({
    apiKey: config.googleApiKey,
    options: {
      types: ["geocode", "establishment"],
      componentRestrictions: { country: config.country },
    },
    onPlaceSelected: (place) => {
      setPlaceId(place.place_id);
    },
  });

  const handlePlaceChange = (e: any) => {
    const parsedAddress = addressParser(e);
    setAddress(undefined);
    setError(undefined);
    let err;

    if (!parsedAddress.Line1) {
      err = getErrorMsg(GooglePlaceError.MISSING_ADDRESS);
    }

    // If name not exist,
    // set street error.
    const isNameExist =
      parsedAddress.Name && parsedAddress.Name !== parsedAddress.PostCode;

    if (!isNameExist) {
      if (!parsedAddress.StreetNumber) {
        err = getErrorMsg(GooglePlaceError.MISSING_STREET_NUMBER);
      }
    }

    if (!parsedAddress.City) {
      err = getErrorMsg(GooglePlaceError.MISSING_CITY);
    }

    if (!parsedAddress.StateOrProvince) {
      err = getErrorMsg(GooglePlaceError.MISSING_STATE_OR_PROVINCE);
    }

    if (!parsedAddress.PostCode) {
      err = getErrorMsg(GooglePlaceError.MISSING_POSTCODE);
    }

    if (!parsedAddress.Lat || !parsedAddress.Long) {
      err = getErrorMsg(GooglePlaceError.MISSING_LAT_OR_LONG);
    }

    setAddress(parsedAddress);
    setError(err);

    onPlaceChange?.({
      ...parsedAddress,
      hasError: err,
    });
  };

  useEffect(() => {
    placesService?.getDetails(
      {
        placeId,
      },
      (placeDetails: any) => {
        handlePlaceChange(placeDetails);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId]);

  const renderAddressText = useCallback(() => {
    if (!address?.PostCode) {
      return null;
    }

    return (
      <Box mt={4} mb={4}>
        {(address?.Name !== address?.PostCode && address?.Name !== address?.Line1) && (
          <Typography>{address?.Name}</Typography>
        )}
        <Typography>{address?.Line1}</Typography>
        <Typography>{address?.City}</Typography>
        <Typography>
          {address?.PostCode} {address?.Line3}
        </Typography>
      </Box>
    );
  }, [address]);

  return (
    <Box>
      <TextFieldInput
        fullWidth
        color="primary"
        variant="outlined"
        inputRef={materialRef}
        error={!!error && Boolean(error?.length > 0)}
        defaultValue={_defaultValue?.Name}
        {...rest}
      />

      {error && (
        <Box style={{ marginTop: -3 }}>
          <Typography
            variant="caption"
            color="error"
            style={{
              fontSize: 14,
              fontWeight: 400,
              letterSpacing: 0.4,
              marginLeft: 14,
            }}
          >
            {error}
          </Typography>
        </Box>
      )}

      {renderAddressText()}
    </Box>
  );
}
